import { useState, useEffect } from "react";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, Handshake } from "lucide-react";

export default function PartnersPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageTitle("Partners");

  useEffect(() => {
    api.get("/partners/grouped").then(res => {
      setGroups(res.data);
      setLoading(false);
    }).catch(() => {
      // Fallback: load all partners ungrouped
      api.get("/content/partners").then(res => {
        if (res.data.length > 0) {
          setGroups([{ category: { name: "Our Partners" }, partners: res.data }]);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    });
  }, []);

  return (
    <div data-testid="partners-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Our Partners</h1>
          <p className="text-white/50 mt-3 text-base">Working together to create positive change</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}
          </div>
        ) : groups.length === 0 || groups.every(g => g.partners.length === 0) ? (
          <div className="text-center py-20">
            <Handshake className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">Partners will be added soon.</p>
          </div>
        ) : (
          <div className="space-y-16" data-testid="partners-groups">
            {groups.filter(g => g.partners.length > 0).map((group, gi) => (
              <div key={gi} data-testid={`partner-group-${gi}`}>
                <h2 className="text-2xl font-bold text-jci-black mb-8 border-l-4 border-jci-blue pl-4">{group.category.name}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {group.partners.map((partner, pi) => (
                    <a
                      key={partner.id}
                      href={partner.website || "#"}
                      target={partner.website ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={`partner-card-${gi}-${pi}`}
                    >
                      <Card className="border shadow-sm hover:shadow-lg transition-shadow h-full group">
                        <CardContent className="p-6 flex flex-col items-center text-center">
                          {partner.logo_url ? (
                            <img src={partner.logo_url} alt={partner.name} className="w-24 h-24 object-contain mb-4" />
                          ) : (
                            <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                              <Handshake className="w-10 h-10 text-slate-300" />
                            </div>
                          )}
                          <h3 className="font-bold text-jci-black text-sm group-hover:text-jci-blue transition-colors">{partner.name}</h3>
                          {partner.website && (
                            <span className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              Visit <ExternalLink className="w-3 h-3" />
                            </span>
                          )}
                        </CardContent>
                      </Card>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
